import React from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Clock,
  Calendar,
  Dog,
  Cat,
  Pill,
  ChevronRight
} from 'lucide-react';

const FollowupAlerts = () => {
  const { followups } = useNotifications();

  if (followups.total_alerts === 0) return null;

  const renderItem = (item, variant) => {
    const colors = {
      overdue: { border: 'border-l-[#D94F4F]', bg: 'bg-[#D94F4F]/5', text: 'text-[#D94F4F]' },
      today: { border: 'border-l-[#C06144]', bg: 'bg-[#C06144]/5', text: 'text-[#C06144]' },
      upcoming: { border: 'border-l-[#4B6651]', bg: 'bg-[#4B6651]/5', text: 'text-[#4B6651]' }
    };
    const c = colors[variant];

    return (
      <Link
        key={item.treatment_id}
        to={`/cases/${item.patient_id}`}
        className={`block p-3 ${c.bg} border-l-4 ${c.border} rounded-r-lg hover:opacity-80 transition-opacity`}
        data-testid={`followup-alert-${item.treatment_id}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {item.species === 'dog' ? <Dog className="w-4 h-4 text-[#C06144]" /> : <Cat className="w-4 h-4 text-[#4B6651]" />}
            <span className="text-sm font-medium text-[#2A2C2A]">{item.patient_name}</span>
          </div>
          <ChevronRight className="w-4 h-4 text-[#5C5F5C]" />
        </div>
        <div className="flex items-center gap-2 mt-1">
          <Pill className="w-3 h-3 text-[#5C5F5C]" />
          <span className="text-xs text-[#5C5F5C]">{item.treatment_name}</span>
        </div>
        <div className="flex items-center gap-1 mt-1">
          <Calendar className="w-3 h-3" />
          <span className={`text-xs font-medium ${c.text}`}>{item.follow_up_date}</span>
        </div>
      </Link>
    );
  };

  return (
    <Card className="border-[#E5E4E0] bg-white" data-testid="followup-alerts">
      <CardHeader className="py-4">
        <CardTitle className="text-base font-['Work_Sans'] flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-[#D94F4F]" />
          Follow-up Reminders
          <span className="ml-auto px-2 py-0.5 bg-[#D94F4F] text-white rounded-full text-xs">
            {followups.total_alerts}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 max-h-[300px] overflow-y-auto">
        {followups.overdue.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-[#D94F4F] uppercase mb-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Overdue ({followups.overdue.length})
            </p>
            <div className="space-y-2">
              {followups.overdue.map(item => renderItem(item, 'overdue'))}
            </div>
          </div>
        )}
        {followups.due_today.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-[#C06144] uppercase mb-2 flex items-center gap-1">
              <Clock className="w-3 h-3" /> Due Today ({followups.due_today.length})
            </p>
            <div className="space-y-2">
              {followups.due_today.map(item => renderItem(item, 'today'))}
            </div>
          </div>
        )}
        {followups.upcoming.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-[#4B6651] uppercase mb-2 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Upcoming ({followups.upcoming.length})
            </p>
            <div className="space-y-2">
              {followups.upcoming.map(item => renderItem(item, 'upcoming'))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FollowupAlerts;
